// Runtime detection of which app to render (therapist vs patient vs israel).
//
// The therapist app, the (UK) patient/psyara app and the (Israel) patient app
// all share a single build/bundle. Which UI is mounted is decided at runtime
// from the hostname, so patients on their own domain never reach the therapist
// login.
//
// Configuration:
//   REACT_APP_PATIENT_HOSTNAMES  comma-separated list of hostnames that should
//                                render the (UK) patient app (overrides the
//                                defaults below when set at build time).
//   REACT_APP_ISRAEL_HOSTNAMES   comma-separated list of hostnames that should
//                                render the Israel patient app (overrides the
//                                defaults below when set at build time).
//   REACT_APP_FORCE_APP          "patient" | "therapist" | "israel" — force a
//                                mode locally (useful for
//                                `REACT_APP_FORCE_APP=israel npm start`).
//   ?app=patient | ?app=therapist | ?app=israel  one-off override; persisted to
//                                sessionStorage so it survives client-side nav.

export type AppTarget = 'therapist' | 'patient' | 'israel';

const DEFAULT_PATIENT_HOSTNAMES = [
  'psyara-health.co.uk',      // prod patient domain (apex)
  'www.psyara-health.co.uk',  // prod patient domain (www)
  'dev.psyara-health.co.uk',  // dev patient domain
];

const DEFAULT_ISRAEL_HOSTNAMES = [
  'dev-my.taliaz-cm.com',     // dev Israel patient domain
  'my.taliaz-cm.com',         // prod Israel patient domain
];

function patientHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_PATIENT_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_PATIENT_HOSTNAMES;
}

function israelHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_ISRAEL_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_ISRAEL_HOSTNAMES;
}

function isValidTarget(value: string | null | undefined): value is AppTarget {
  return value === 'patient' || value === 'therapist' || value === 'israel';
}

function readOverride(): AppTarget | null {
  // Build-time / env override (local dev).
  if (isValidTarget(process.env.REACT_APP_FORCE_APP)) {
    return process.env.REACT_APP_FORCE_APP as AppTarget;
  }

  // Query-param override, persisted so it survives in-app navigation.
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('app');
    if (isValidTarget(fromQuery)) {
      window.sessionStorage.setItem('appMode', fromQuery);
      return fromQuery;
    }
    const stored = window.sessionStorage.getItem('appMode');
    if (isValidTarget(stored)) return stored;
  } catch {
    // sessionStorage may be unavailable; fall through to hostname detection.
  }
  return null;
}

// Resolve which app should be mounted for the current request.
export function resolveApp(): AppTarget {
  const override = readOverride();
  if (override) return override;

  const hostname = window.location.hostname.toLowerCase();
  if (israelHostnames().includes(hostname)) return 'israel';
  if (patientHostnames().includes(hostname)) return 'patient';
  return 'therapist';
}

// patient = the UK psyara patient app.
export function isPatientApp(): boolean {
  return resolveApp() === 'patient';
}

// israel = the Israel-clinic patient app.
export function isIsraelApp(): boolean {
  return resolveApp() === 'israel';
}

// Which staff app a login belongs to. The clinic and HMO apps share one
// backend, and the backend gates login per app: the clinic app needs the
// clinic_login privilege flag, the HMO app needs the hmo flag.
//
//   REACT_APP_HMO_HOSTNAMES  comma-separated hostnames served by the HMO app
//                            (overrides the defaults below when set).
//   ?loginApp=hmo | ?loginApp=clinic  one-off override for local testing.
export type LoginApp = 'clinic' | 'hmo';

const DEFAULT_HMO_HOSTNAMES = [
  'dev-hmo.taliaz-cm.com',    // dev HMO domain
  'hmo.taliaz-cm.com',        // prod HMO domain
];

function hmoHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_HMO_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_HMO_HOSTNAMES;
}

export function resolveLoginApp(): LoginApp {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('loginApp');
    if (fromQuery === 'hmo' || fromQuery === 'clinic') return fromQuery;
  } catch {
    // Fall through to hostname detection.
  }
  return hmoHostnames().includes(window.location.hostname.toLowerCase()) ? 'hmo' : 'clinic';
}
